import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getUserInfo } from 'Utils';
import * as analytics from 'Utils/analytics';
import { addUserFavorite, deleteUserFavorite } from 'Services/library';
import { getUserRecommendations } from 'Services/recommendations';
import RecommendationsList from 'Components/profile/RecommendationsList';
const { EVENTS } = analytics;
const FETCH_COUNT = 20;

const Recommendations = ({
  country,
  dispatchToast,
  favoritesType,
  interestsBased,
  language,
  strings,
  type,
  uim,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [itemList, setItemList] = useState([]);
  const [itemMap, setItemMap] = useState({});
  const [totalCount, setTotalCount] = useState(0);

  const reset = () => {
    setIsLoading(true);
    setHasError(false);
    setItemList([]);
    setItemMap({});
    setTotalCount(0);
  };

  const getRecommendations = async () => {
    try {
      const { userId, userToken } = await getUserInfo(uim);
      const {
        isCancel,
        resourceCount,
        resources,
      } = await getUserRecommendations({
        favoritesType,
        interestsBased,
        limit: FETCH_COUNT,
        locale: `${language}-${country}`,
        type,
        userId,
        userToken,
      });

      // Fetching user tab library can be cancelled by quickly switching tabs,
      // the below ensures the loading state remains active in that case
      if (isCancel) return;

      const itemList = [];
      const itemMap = {};
      resources.forEach((item) => {
        if (!itemList.includes(item.id)) itemList.push(item.id);
        itemMap[item.id] = item;
      });
      setIsLoading(false);
      setHasError(false);
      setItemList(itemList);
      setItemMap(itemMap);
      setTotalCount(resourceCount);
    } catch ({ message }) {
      console.error(`Error fetching '${type}' recommended content: ${message}`);
      dispatchToast({
        message: strings.recommendationsError,
      });
      setIsLoading(false);
      setHasError(true);
    }
  };

  const updateFavoriteStatus = (id, status) => {
    setItemMap((itemMap) => {
      const item = itemMap[id];
      return {
        ...itemMap,
        [id]: {
          ...item,
          isSaved: status,
        },
      };
    });
  };

  const addFavoriteRecommendation = async (contentId) => {
    const analyticsEvent = interestsBased
      ? EVENTS.addInterestsRecommendationToLibrary
      : EVENTS.addRecommendationToLibrary;
    analytics.event({ action: analyticsEvent, label: contentId });
    try {
      updateFavoriteStatus(contentId, true);
      const { userId, userToken } = await getUserInfo(uim);
      await addUserFavorite({ contentId, userId, userToken });
    } catch ({ message }) {
      updateFavoriteStatus(contentId, false);
      console.error(
        `Error adding '${contentId}' to favorited content: ${message}`
      );
    }
  };

  const deleteFavoriteRecommendation = async (contentId) => {
    try {
      updateFavoriteStatus(contentId, false);
      const { userId, userToken } = await getUserInfo(uim);
      await deleteUserFavorite({ contentId, userId, userToken });
    } catch ({ message }) {
      updateFavoriteStatus(contentId, true);
      console.error(
        `Error removing '${contentId}' from favorited content: ${message}`
      );
    }
  };

  const handleFavoriteRecommendation = async (contentId) => {
    const { isSaved } = itemMap[contentId];
    return isSaved
      ? deleteFavoriteRecommendation(contentId)
      : addFavoriteRecommendation(contentId);
  };

  useEffect(() => {
    reset();
    getRecommendations();
  }, [type]);

  return (
    <div className="recommendations">
      <RecommendationsList
        country={country}
        hasError={hasError}
        isLoading={isLoading}
        interestsBased={interestsBased}
        itemList={itemList}
        itemMap={itemMap}
        onFavoriteRecommendation={handleFavoriteRecommendation}
        strings={strings}
        totalCount={totalCount}
        type={type}
      />
    </div>
  );
};

Recommendations.propTypes = {
  country: PropTypes.string.isRequired,
  dispatchToast: PropTypes.func.isRequired,
  favoritesType: PropTypes.string.isRequired,
  interestsBased: PropTypes.bool,
  language: PropTypes.string.isRequired,
  strings: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  uim: PropTypes.object.isRequired,
};

Recommendations.defaultProps = {
  interestsBased: false,
};

export default Recommendations;
